import { useMemo } from "react";
import { AuditRow } from "../models/AuditRow";
import { AuditTrip } from "../models/AuditTrip";
import { AuditView } from "../models/AuditView";
import { buildDaySummaryRows } from "../utils/daySummaries";
import { normalizeReason } from "../utils/reasonTaxClass";
import { REASON_NA } from "@/constants/constants";

interface Args {
  trips: AuditTrip[];
  view: AuditView;
  reasons: string[];
  timeZone: string;
}

/**
 * Applies the reason filter and builds the day rows of the current view from that view's entries
 * only, so an analog day shows nothing but the drivers' entries and a digital day nothing but
 * Geotab's figures. Every day figure is the sum of the listed entries. Unbooked trips are listed
 * in the digital view like in the period summary, so table and summary add up to the same kilometres.
 */
export function useAuditRows({ trips, view, reasons, timeZone }: Args) {
  const summaryTrips = useMemo(() => {
    if (reasons.length === 0) return trips;
    const includeMissing = reasons.includes(REASON_NA);
    const wanted = new Set(reasons.filter(r => r !== REASON_NA).map(normalizeReason));
    return trips.filter(trip =>
      trip.reason ? wanted.has(normalizeReason(trip.reason)) : includeMissing,
    );
  }, [trips, reasons]);

  const counts = useMemo(() => {
    let digital = 0;
    let analog = 0;
    for (const trip of summaryTrips) {
      if (trip.recordType === "analog") analog++;
      else digital++;
    }
    return { digital, analog, total: digital + analog };
  }, [summaryTrips]);

  const viewTrips = useMemo(() => summaryTrips.filter(trip => trip.recordType === view), [summaryTrips, view]);

  const rows: AuditRow[] = useMemo(() => buildDaySummaryRows(viewTrips, timeZone), [viewTrips, timeZone]);

  return { rows, viewTrips, summaryTrips, counts };
}
